/**
 * storeDisplayName.ts — the brand name shown to humans (browser tab title,
 * page <title>, share previews).
 *
 * Why this exists: the tab title used to come straight from `company.name`,
 * and the `balasistore_company` document has no `name` field at all — so the
 * tab literally read "undefined". The prerendered pages had the mirror problem:
 * they fell back to the raw store id ("balasistore_store").
 *
 * The map keeps the display name next to the code that renders it (same
 * per-store keying pattern as `getSellerIdentity` on the backend), so no
 * Firestore change is required to fix a customer-visible name.
 *
 * NOTE: this module is imported from build-time (Node) code as well as the
 * browser bundle — keep it free of browser globals and side effects.
 */

const STORE_DISPLAY_NAMES: Partial<Record<string, string>> = {
	balasistore_store: "בלסי סטור אונליין",
};

/**
 * The one-line pitch used as `<meta name="description">` / `og:description` on
 * the home page — i.e. the grey snippet under the blue link in Google results.
 *
 * Without an entry here the snippet falls back to "<brand> — חנות אונליין",
 * which is generic and repeats the title; Google then usually ignores it and
 * invents its own text from the page. Keep each entry a single sentence of
 * ~110–150 chars, and keep it truthful to the storefront copy.
 */
const STORE_DESCRIPTIONS: Partial<Record<string, string>> = {
	balasistore_store:
		"בלסי סטור אונליין — אספקה למשרדים במרכז הארץ. מוצרים איכותיים במחירים הוגנים, הזמנה אחת ומשלוח אחד מסודר.",
};

interface StoreDisplayNameSources {
	/** Store id, e.g. "balasistore_store". */
	storeId?: string;
	/** `store.name` from Firestore, e.g. "BALASI STORE". */
	storeName?: string;
	/** `company.name` from Firestore — often missing. */
	companyName?: string;
}

/**
 * Resolve the human-facing brand name, most specific source first:
 * per-store override → store document name → company document name → "".
 *
 * Never returns "undefined"/"null" as text — an unknown store yields "" so the
 * caller can fall back to its own generic title.
 */
export function getStoreDisplayName({
	storeId,
	storeName,
	companyName,
}: StoreDisplayNameSources): string {
	const override = storeId ? STORE_DISPLAY_NAMES[storeId] : undefined;
	return override ?? storeName ?? companyName ?? "";
}

/**
 * Resolve the home-page search snippet: per-store override → generic
 * "<brand> — חנות אונליין" for stores that have no copy of their own.
 */
export function getStoreDescription({
	storeId,
	storeName,
	companyName,
}: StoreDisplayNameSources): string {
	const override = storeId ? STORE_DESCRIPTIONS[storeId] : undefined;
	if (override) return override;

	const brand = getStoreDisplayName({ storeId, storeName, companyName });
	return brand ? `${brand} — חנות אונליין` : "";
}
